import React, { useEffect, useState, useRef, useCallback } from 'react';
import useEsami from '../../hooks/useEsami';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/it';
import CustomCalendar from '../../components/CustomCalendar/CustomCalendar';
import CustomModal from '../../components/CustomModal/CustomModal';
import { Button, Alert, Badge, Card, Table } from 'react-bootstrap';
import ComebackArrow from '../../components/ComebackArrow/ComebackArrow';
import CardLoadingPage from '../../components/CardLoadingPage/CardLoadingPage';
import useEsamiUtenti from '../../hooks/useEsamiUtenti';
import EsamiUtentiService from '../../services/EsamiUtentiService/EsamiUtentiService';
import { alertService } from '../../services/AlertService';
import { useSelector } from 'react-redux';
import { ROLES } from '../../model/roles';
import useMediaQuery from '../../hooks/useMediaQuery';
import CustomButton from '../../components/CustomButton/CustomButton';
import { TIPO_CORSO } from '../../model/tipoCorso';

const DEVICE_TEST_JAMPINK_OPENED_KEY = 'highedu_device_test_opened_jampink';
const PENDING_EXAM_BOOKING_KEY = 'highedu_pending_exam_device_test';

const deviceTestStorage = {
  getItem: (key) => localStorage.getItem(key) || sessionStorage.getItem(key),
  setItem: (key, value) => {
    localStorage.setItem(key, value);
    sessionStorage.setItem(key, value);
  },
  removeItem: (key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  },
};

/** URL stabile per return_page Jampink: senza query/hash che possono alterare il token ?se= */
const getDeviceTestReturnPage = () => {
  const { origin, pathname } = window.location;
  return `${origin}${pathname}`;
};

const isValidDeviceTestUrl = (url) => typeof url === 'string' && /[?&]se=/.test(url);

const isDeviceTestOkPassed = (response) => Number(response?.is_device_test_ok) === 1;

/** Sessione chiusa da flag DB (`is_concluso`) o perché l'orario di fine sessione è già passato.
 *  Replicata da EsamiConfigPage (lato admin) per coerenza sul corsista. */
function isSessioneEsameStorica(exam) {
  if (!exam) return false;
  if (Number(exam.is_concluso) === 1 || exam.is_concluso === true) return true;
  const dataSolo = exam.data ? String(exam.data).split(' ')[0] : '';
  const oraFine = exam.ora_fine;
  if (!dataSolo || oraFine == null || String(oraFine).trim() === '') return false;
  const timePart = String(oraFine).trim();
  let end = moment(
    `${dataSolo} ${timePart}`,
    ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD H:mm', 'YYYY-MM-DD HH:mm:ss.SSS'],
    true
  );
  if (!end.isValid()) {
    end = moment(`${dataSolo}T${timePart}`);
  }
  if (!end.isValid()) return false;
  return !moment().isBefore(end);
}

const isUserExamBooked = (exam) =>
  exam != null && (Number(exam.prenotato) === 1 || exam.prenotato === true || Number(exam.myPrenotation) === 1);

const normalizeExamDates = (exam) => ({
  ...exam,
  start: new Date(`${exam.data.split(' ')[0]}T${exam.ora_inizio}`),
  end: new Date(`${exam.data.split(' ')[0]}T${exam.ora_fine}`),
  myPrenotation: Number(exam.prenotato) === 1 || exam.prenotato === true ? 1 : exam.myPrenotation,
  prenotato: Number(exam.prenotato) === 1 || exam.prenotato === true ? 1 : exam.prenotato,
});

/** Unisce esami disponibili e prenotati del corsista (priorità ai dati prenotazione). */
const mergeExamsForStudent = (available, booked) => {
  const byId = new Map();
  for (const exam of available || []) {
    if (exam?.id_calendario_esame != null) {
      byId.set(exam.id_calendario_esame, exam);
    }
  }
  for (const exam of booked || []) {
    if (exam?.id_calendario_esame != null) {
      byId.set(exam.id_calendario_esame, { ...byId.get(exam.id_calendario_esame), ...exam });
    }
  }
  return [...byId.values()];
};

/** Avviso corsista: la prenotazione è attestata dall'email di conferma (contenuto istruzioni). */
function AlertEmailConfermaPrenotazioneEsame({ className = 'mb-3' }) {
  return (
    <Alert variant="info" className={className}>
      <p className="mb-0 small">
        <strong>Email di conferma.</strong>{' '}
        Alla prenotazione riceverai un&apos;email di conferma all&apos;indirizzo associato al tuo account:
        è quella che{' '}
        <strong>fa fede per la prenotazione</strong>. Nella stessa email sono riportate tutte le
        spiegazioni e le istruzioni alle quali dovrai attenerti.
      </p>
    </Alert>
  );
}

export default function AreaPersonaleEsamiPage() {
  const { getEsamiByClasseAndByToken, loadingStateEsami, getEsamiByIdClasse, getAllEsamiByIdClasse } = useEsami();
  const { prenotaEsameCorsoSincrono, loadingStateEsamiUtenti, prenotaEsameCorsoAsincrono, deleteEsameUtente, enterJoinroomCorsista, startDeviceTestPrenotazioneEsame, checkDeviceTestPrenotazioneEsame } = useEsamiUtenti();
  const location = useLocation();
  const classe = location.state?.classeData;
  const [exams, setExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [bookingStep, setBookingStep] = useState('initial');
  const userLoggedIn = useSelector(state => state?.user?.userData);
  const isSmallScreen = useMediaQuery('(max-width: 1024px)');
  const [examCountdowns, setExamCountdowns] = useState({});
  const [errorMessage, setErrorMessage] = useState({});

  const [showAccessDeniedModal, setShowAccessDeniedModal] = useState(false);
  const [accessDeniedMessage, setAccessDeniedMessage] = useState("");

  // Modale di accesso alla stanza virtuale: dopo la chiamata API mostriamo
  // un pulsante <a target="_blank"> che l'utente clicca lui stesso per aprire
  // jampink in una nuova scheda. In questo modo:
  //  - il browser non blocca il popup (è un click utente diretto su un anchor),
  //  - non si esce dal dominio servito dal reverse proxy (HighEdu resta aperto
  //    nella scheda originale).
  const [showOpenRoomModal, setShowOpenRoomModal] = useState(false);
  const [meetingUrl, setMeetingUrl] = useState("");
  const [showDeviceTestModal, setShowDeviceTestModal] = useState(false);
  const [showDeviceTestFailedModal, setShowDeviceTestFailedModal] = useState(false);
  const [deviceTestUrl, setDeviceTestUrl] = useState("");
  const [deviceTestPolling, setDeviceTestPolling] = useState(false);
  const deviceTestPollRef = useRef(null);
  const deviceTestPassedRef = useRef(false);

  const stopDeviceTestPolling = useCallback(() => {
    if (deviceTestPollRef.current) {
      clearInterval(deviceTestPollRef.current);
      deviceTestPollRef.current = null;
    }
    setDeviceTestPolling(false);
  }, []);

  useEffect(() => () => stopDeviceTestPolling(), [stopDeviceTestPolling]);

  const pendingReturnCheckRef = useRef(false);

  const persistPendingExamBooking = (exam) => {
    deviceTestStorage.setItem(PENDING_EXAM_BOOKING_KEY, JSON.stringify({
      id_calendario_esame: exam.id_calendario_esame,
      examSnapshot: {
        id_calendario_esame: exam.id_calendario_esame,
        start: exam.start instanceof Date ? exam.start.toISOString() : exam.start,
        end: exam.end instanceof Date ? exam.end.toISOString() : exam.end,
        data: exam.data,
        ora_inizio: exam.ora_inizio,
        ora_fine: exam.ora_fine,
        prenotato: exam.prenotato,
      },
    }));
  };

  const clearPendingExamBooking = () => {
    deviceTestStorage.removeItem(PENDING_EXAM_BOOKING_KEY);
    deviceTestStorage.removeItem(DEVICE_TEST_JAMPINK_OPENED_KEY);
  };

  const markDeviceTestJampinkOpened = () => {
    deviceTestStorage.setItem(DEVICE_TEST_JAMPINK_OPENED_KEY, '1');
  };

  const hasReturnedFromDeviceTestJampink = () =>
    deviceTestStorage.getItem(DEVICE_TEST_JAMPINK_OPENED_KEY) === '1'
    || (typeof document !== 'undefined' && document.referrer.includes('jampink.com'));

  const restoreExamFromPending = (pending) => {
    const fromList = exams.find((e) => e.id_calendario_esame === pending.id_calendario_esame);
    if (fromList) return fromList;
    if (!pending.examSnapshot) return null;
    return {
      ...pending.examSnapshot,
      start: new Date(pending.examSnapshot.start),
      end: new Date(pending.examSnapshot.end),
    };
  };

  /** Ritorno da Jampink (return_page): se il test è ok, apri direttamente la modale di conferma. */
  useEffect(() => {
    if (pendingReturnCheckRef.current || !classe?.id_classe_utente) return;

    const raw = deviceTestStorage.getItem(PENDING_EXAM_BOOKING_KEY);
    if (!raw) return;

    let pending;
    try {
      pending = JSON.parse(raw);
    } catch {
      clearPendingExamBooking();
      return;
    }

    const exam = restoreExamFromPending(pending);
    if (!exam) return;

    if (!hasReturnedFromDeviceTestJampink()) return;

    pendingReturnCheckRef.current = true;

    (async () => {
      try {
        const res = await checkDeviceTestPrenotazioneEsame({
          id_calendario_esame: pending.id_calendario_esame,
          id_classe_utente: classe.id_classe_utente,
        });
        deviceTestStorage.removeItem(DEVICE_TEST_JAMPINK_OPENED_KEY);
        if (isDeviceTestOkPassed(res)) {
          deviceTestPassedRef.current = true;
          setSelectedExam(exam);
          setShowModal(true);
          setBookingStep('confirm');
          alertService.success('Test dispositivo superato. Puoi confermare la prenotazione.', { autoClose: true });
        } else {
          setSelectedExam(exam);
          setShowDeviceTestFailedModal(true);
        }
      } catch (error) {
        console.error('Errore verifica device test al ritorno da Jampink:', error);
      }
    })();
  }, [classe?.id_classe_utente, exams, checkDeviceTestPrenotazioneEsame]);

  useEffect(() => {
    if (classe?.id_classe) {
      if (userLoggedIn?.role === ROLES.TUTOR) {
        fetchEsamiForTutor();
      } else {
        if (classe?.corso?.modalita_erogazione !== TIPO_CORSO.TELEMATICA_ASINCRONA) {
          fetchGetEsamiByClasseAndByToken({ id_classe: classe.id_classe });
        } else {
          fetchAllEsamiByIdClasse({ id_classe: classe.id_classe });
        }
      }
    }
  }, [classe]);

  useEffect(() => {
    moment.locale('it');
  }, []);

  const fetchGetEsamiByClasseAndByToken = async (classId) => {
    let response = await getEsamiByClasseAndByToken(classId);
    if (Array.isArray(response)) {
      const nextExams = response.map(normalizeExamDates);
      setExams(nextExams);
      return nextExams;
    }
    return [];
  };

  const fetchAllEsamiByIdClasse = async (classId) => {
      const [resEsamiByClasseAndByToken, resAllEsamiByIdClasse] = await Promise.all([
        getEsamiByClasseAndByToken(classId),
        getAllEsamiByIdClasse(classId)
      ]);

      if (Array.isArray(resAllEsamiByIdClasse) && Array.isArray(resEsamiByClasseAndByToken)) {
        const nextExams = mergeExamsForStudent(resAllEsamiByIdClasse, resEsamiByClasseAndByToken).map(normalizeExamDates);
        setExams(nextExams);
        return nextExams;
      }
      return [];
  };

  const fetchEsamiForTutor = async () => {
      const response = await getEsamiByIdClasse({ id_classe: classe.id_classe });
      if (Array.isArray(response)) {
        setExams(response.map(exam => ({
          ...exam,
          start: new Date(`${exam.data.split(' ')[0]}T${exam.ora_inizio}`),
          end: new Date(`${exam.data.split(' ')[0]}T${exam.ora_fine}`),
          title: `Esame (${exam.utenti.filter(u => u.prenotato).length}/${exam.utenti.length} prenotati)`
        })));
      }
  };

  const handleSelectEvent = (event) => {
    setSelectedExam(event);
    setShowModal(true);
    setBookingStep('initial');
  };

  const handleCloseModal = () => {
    stopDeviceTestPolling();
    setShowDeviceTestModal(false);
    setDeviceTestUrl("");
    setShowModal(false);
    setSelectedExam(null);
    setBookingStep('initial');
  };

  const openBookingConfirmAfterDeviceTest = useCallback((exam) => {
    deviceTestPassedRef.current = true;
    deviceTestStorage.removeItem(DEVICE_TEST_JAMPINK_OPENED_KEY);
    setSelectedExam(exam);
    setShowDeviceTestModal(false);
    setDeviceTestUrl('');
    setShowModal(true);
    setBookingStep('confirm');
    alertService.success('Test dispositivo superato. Puoi confermare la prenotazione.', { autoClose: true });
  }, []);

  const pollDeviceTestStatus = useCallback((idCalendarioEsame, exam) => {
    stopDeviceTestPolling();
    setDeviceTestPolling(true);
    deviceTestPassedRef.current = false;

    const tick = async () => {
      try {
        const res = await checkDeviceTestPrenotazioneEsame({
          id_calendario_esame: idCalendarioEsame,
          id_classe_utente: classe.id_classe_utente,
        }, { silent: true });
        if (isDeviceTestOkPassed(res)) {
          stopDeviceTestPolling();
          openBookingConfirmAfterDeviceTest(exam);
        }
      } catch (error) {
        console.error('Errore polling device test:', error);
      }
    };

    tick();
    deviceTestPollRef.current = setInterval(tick, 3000);
  }, [checkDeviceTestPrenotazioneEsame, classe?.id_classe_utente, stopDeviceTestPolling, openBookingConfirmAfterDeviceTest]);

  const startDeviceTestForBooking = async (exam) => {
    if (!exam || userLoggedIn?.role === ROLES.TUTOR) {
      setSelectedExam(exam);
      setShowModal(true);
      setBookingStep('confirm');
      return;
    }

    try {
      stopDeviceTestPolling();
      deviceTestPassedRef.current = false;
      setSelectedExam(exam);
      persistPendingExamBooking(exam);
      setShowModal(false);

      const response = await startDeviceTestPrenotazioneEsame({
        id_calendario_esame: exam.id_calendario_esame,
        id_classe_utente: classe.id_classe_utente,
        return_page: getDeviceTestReturnPage(),
      });

      if (response?.device_test_url && isValidDeviceTestUrl(response.device_test_url)) {
        setDeviceTestUrl(response.device_test_url);
        setShowDeviceTestModal(true);
        pollDeviceTestStatus(exam.id_calendario_esame, exam);
      } else {
        alertService.error(
          response?.device_test_url
            ? 'URL test dispositivo non valido (manca il token Jampink ?se=). Verifica il deploy del backend.'
            : 'Impossibile avviare il test dispositivo.'
        );
      }
    } catch (error) {
      alertService.error(error?.message || 'Errore durante l\'avvio del test dispositivo.');
    }
  };

  const handleBookExam = () => startDeviceTestForBooking(selectedExam);

  const handleConfirmBooking = async () => {
    try {
      let response;
      if(classe?.corso?.modalita_erogazione === TIPO_CORSO.TELEMATICA_ASINCRONA) { 
        response = await prenotaEsameCorsoAsincrono({
          id_calendario_esame: selectedExam.id_calendario_esame,
          id_classe_utente: classe.id_classe_utente,
          require_device_test: 1,
        });
      } else {
        response = await prenotaEsameCorsoSincrono({
          id_calendario_esame: selectedExam.id_calendario_esame,
          id_classe_utente: classe.id_classe_utente,
          require_device_test: 1,
        });
      }
  
      if (response) {
        if(!response.success){
          setErrorMessage(response.message);
          setShowErrorModal(true);
          setShowModal(false);
          return;
        }
        let refreshedExams;
        if (classe?.corso?.modalita_erogazione !== TIPO_CORSO.TELEMATICA_ASINCRONA) {
          refreshedExams = await fetchGetEsamiByClasseAndByToken({ id_classe: classe.id_classe });
        } else {
          refreshedExams = await fetchAllEsamiByIdClasse({ id_classe: classe.id_classe });
        }
        const updatedExam = refreshedExams?.find((e) => e.id_calendario_esame === selectedExam.id_calendario_esame);
        if (updatedExam) {
          setSelectedExam(updatedExam);
        }
        setBookingStep('success');
        clearPendingExamBooking();
      }
    } catch (error) {
      setErrorMessage(error.message || "Si è verificato un errore imprevisto.");
      setShowErrorModal(true);
    }
  };

  const openCancelModal = () => {
    setShowCancelModal(true);
  };
  
  const confirmCancelBooking = async () => {
    try {
      await deleteEsameUtente({
        id_esame_utente: selectedExam.id_calendario_esame_utente
      });
  
      if (classe?.corso?.modalita_erogazione !== TIPO_CORSO.TELEMATICA_ASINCRONA) {
        await fetchGetEsamiByClasseAndByToken({ id_classe: classe.id_classe });
      } else {
        await fetchAllEsamiByIdClasse({ id_classe: classe.id_classe });
      }
  
      alertService.success("Prenotazione annullata con successo.", { autoClose: true });
      setShowModal(false);
      setShowCancelModal(false); // Chiudi anche la modale di conferma
    } catch (error) {
      alertService.error('Errore durante l\'annullamento della prenotazione.');
    }
  };

  const minDate = classe?.data_fine ? moment(classe.data_fine).toDate() : new Date();

  const customEventPropGetter = (event) => {
    let style = {
      backgroundColor: '#3174ad',
      color: 'white'
    };

    if (isUserExamBooked(event)) {
      if (event.presenza === 'assente') {
        style.backgroundColor = '#dc3545';
      } else if (event.esito_esame) {
        if (event.esito_esame === 'promosso') {
          style.backgroundColor = '#28a745';
        } else if (event.esito_esame === 'bocciato') {
          style.backgroundColor = '#ffc107';
          style.color = 'black';
        }
      } else {
        style.backgroundColor = '#3174ad';
      }
    }

    return { style };
  };

  useEffect(() => {
    updateCountdowns(); // chiamata iniziale
    const interval = setInterval(() => {
      updateCountdowns();
    }, 1000);
  
    return () => clearInterval(interval);
  }, [exams]);

  const updateCountdowns = () => {
    const now = moment();
    const updatedCountdowns = {};
  
    exams.forEach(exam => {
      if (isUserExamBooked(exam)) {
        const start = moment(exam.start);
        const diffSeconds = start.diff(now, 'seconds');
  
        if (diffSeconds <= 0) {
          updatedCountdowns[exam.id_calendario_esame] = 'INIZIATO';
        } else if (diffSeconds <= 86400) { // solo se mancano meno di 24 ore
          if (diffSeconds < 60) {
            updatedCountdowns[exam.id_calendario_esame] = `Inizio tra ${diffSeconds} second${diffSeconds === 1 ? 'o' : 'i'}`;
          } else if (diffSeconds < 3600) {
            const minutes = Math.floor(diffSeconds / 60);
            updatedCountdowns[exam.id_calendario_esame] = `Inizio tra ${minutes} minut${minutes === 1 ? 'o' : 'i'}`;
          } else {
            const duration = moment.duration(start.diff(now));
            const hours = duration.hours();
            const minutes = duration.minutes();
            updatedCountdowns[exam.id_calendario_esame] = `Inizio tra ${hours} or${hours === 1 ? 'a' : 'e'} e ${minutes} minut${minutes === 1 ? 'o' : 'i'}`;
          }
        }
      }
    });
  
    setExamCountdowns(prev => {
      // Interrompi aggiornamenti quando un esame è INIZIATO
      const changed = Object.keys(updatedCountdowns).some(
        key => updatedCountdowns[key] !== prev[key]
      );
      return changed ? updatedCountdowns : prev;
    });
  };

  /*
  const enterJoinRoom = async (id_calendario_esame) => {
    
    // --- INIZIO FIX POPUP BLOCKER ---

    // 1. Apri la finestra VUOTA immediatamente al click (sincrono).
    // Questo inganna il popup blocker.
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write('<p>Accesso alla meeting room in corso, attendere prego...</p>');
    } else {
      // Se newWindow è null, il popup blocker è comunque intervenuto
      alertService.error('Il browser ha bloccato l\'apertura della finestra. Abilita i popup per questo sito e riprova.');
      return;
    }

    try {
      // 2. Esegui la chiamata async come prima
      let response;
      response = await enterJoinroomCorsista({ id_calendario_esame: id_calendario_esame, id_classe_utente: classe.id_classe_utente });
      var meeting_id = response[0].meeting_id;
      var enc_str = response[0].enc_str;

      if (meeting_id.length > 0) {
        const meetingUrl = `https://www.jampink.com/jp-meeting/${meeting_id}?se=${enc_str}`;
        
        // 3. REINDIRIZZA la finestra già aperta invece di aprirne una nuova
        newWindow.location.href = meetingUrl;
      
      } else {
        // Se non c'è meeting_id, chiudi la finestra e mostra un errore
        newWindow.close();
        alertService.error('Impossibile recuperare i dati della meeting room.');
      }
    } catch (error) {
      // 4. In caso di errore, chiudi la finestra e avvisa l'utente
      if (newWindow) {
        newWindow.close();
      }
      alertService.error('Errore durante l\'accesso alla Joinroom di esame.');
    }
    // --- FINE FIX POPUP BLOCKER ---
  };
  */
  const enterJoinRoom = async (id_calendario_esame) => {
    // Strategia anti-popup-blocker e compatibile con reverse proxy:
    // 1) facciamo la chiamata API per ottenere meeting_id ed enc_str;
    // 2) salviamo l'URL della stanza in stato e apriamo una modale con un
    //    link <a target="_blank">. Sarà l'utente stesso a cliccarlo: questo
    //    è considerato dal browser un'azione utente diretta, quindi non viene
    //    mai bloccato come popup, e l'apertura avviene in una nuova scheda
    //    (così il dominio servito dal reverse proxy resta sulla scheda HighEdu).
    try {
      let response = await enterJoinroomCorsista({
        id_calendario_esame: id_calendario_esame,
        id_classe_utente: classe.id_classe_utente
      });

      // --- GESTIONE BLOCCO ACCESSO ---
      // Il backend può rispondere come oggetto { access_denied: true }
      // oppure come array [ { access_denied: true } ]: copriamo entrambi.
      const responseData = Array.isArray(response) ? response[0] : response;

      if (responseData && (responseData.access_denied === true || response.access_denied === true)) {
        const msg = responseData.message || response.message || "Accesso negato.";
        setAccessDeniedMessage(msg);
        setShowAccessDeniedModal(true);
        return;
      }
      // -------------------------------

      const meeting_id = response[0]?.meeting_id;
      const enc_str = response[0]?.enc_str;

      if (meeting_id && meeting_id.length > 0) {
        const url = `https://www.jampink.com/jp-meeting/${meeting_id}?se=${enc_str}`;
        setMeetingUrl(url);
        setShowOpenRoomModal(true);
      } else {
        alertService.error('Impossibile recuperare i dati della meeting room.');
      }
    } catch (error) {
      console.error("Errore joinroom:", error);
      alertService.error('Errore durante l\'accesso alla Joinroom di esame.');
    }
  };


  if (loadingStateEsami.isLoadingEsamiByClasseAndByToken) {
    return <CardLoadingPage />;
  }

  const renderModalContent = () => {
    if (!selectedExam) return null;

    if (userLoggedIn?.role === ROLES.TUTOR) {
      return (
        <div>
          <h4>Dettagli Esame</h4>
          <p>Data: {moment(selectedExam.start).format('DD/MM/YYYY')}</p>
          <p>Orario: {moment(selectedExam.start).format('HH:mm')} - {moment(selectedExam.end).format('HH:mm')}</p>
          <h5>Partecipanti:</h5>
          {isSmallScreen ? (
            <div>
              {selectedExam.utenti?.map(utente => (
                <Card key={utente.id_utente} className="mb-3">
                  <Card.Body>
                    <Card.Title>{utente.nome} {utente.cognome}</Card.Title>
                    <Card.Text>
                      <strong>Stato Prenotazione:</strong> {utente.prenotato ? 'Prenotato' : 'Non prenotato'}<br/>
                      <strong>Presenza:</strong> {
                        utente.presenza === 'presente' ? 'Presente' :
                        utente.presenza === 'assente' ? 'Assente' :
                        'Non registrata'
                      }<br/>
                      <strong>Esito:</strong> {utente.esito_esame?.replace('_', ' ') || 'Non svolto'}<br/>
                      <strong>Voto:</strong> {utente.voto || '-'}
                    </Card.Text>
                  </Card.Body>
                </Card>
              ))}
            </div>
          ) : (
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Cognome</th>
                  <th>Stato Prenotazione</th>
                  <th>Presenza</th>
                  <th>Esito</th>
                  <th>Voto</th>
                </tr>
              </thead>
              <tbody>
                {selectedExam.utenti?.map(utente => (
                  <tr key={utente.id_utente}>
                    <td>{utente.nome}</td>
                    <td>{utente.cognome}</td>
                    <td>{utente.prenotato ? 'Prenotato' : ''}</td>
                    <td>
                      {utente.presenza === 'presente' ? 'Presente' :
                       utente.presenza === 'assente' ? 'Assente' :
                       'Non registrata'}
                    </td>
                    <td>{utente.esito_esame?.replace('_', ' ') || 'Non svolto'}</td>
                    <td>{utente.voto || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      );
    }

    const renderExamResult = () => {
      if (selectedExam.presenza === 'assente') {
        return (
          <Alert variant="danger">
            <Alert.Heading><i className="bi bi-exclamation-triangle me-2"></i>Assente all'esame</Alert.Heading>
            <p>Non hai partecipato a questo esame.</p>
            {selectedExam.note && (
              <>
              <hr />
              <p className="mb-0"><strong>Note:</strong> {selectedExam.note}</p>
              </>
            )}
          </Alert>
        );
      } else if (selectedExam.esito_esame && selectedExam.esito_esame !== 'non_svolto') {
        const isPromosso = selectedExam.esito_esame === 'promosso';
        return (
          <Card className={`border-${isPromosso ? 'success' : 'warning'} mb-3`}>
            <Card.Header className={`bg-${isPromosso ? 'success' : 'warning'} ${isPromosso ? 'text-white' : 'text-dark'}`}>
              <h5 className="mb-0">
                {isPromosso ? (
                  <><i className="bi bi-check-circle me-2"></i> Esame Superato</>
                ) : (
                  <><i className="bi bi-x-circle me-2"></i> Esame Non Superato</>
                )}
              </h5>
            </Card.Header>
            <Card.Body>
              <Card.Title>
                Esito: <span>{selectedExam.esito_esame.charAt(0).toUpperCase() + selectedExam.esito_esame.slice(1)}</span>
              </Card.Title>
              {selectedExam.voto && (
                <Card.Text>
                  <strong>Voto:</strong> <span className="fs-4">{selectedExam.voto}</span>
                </Card.Text>
              )}
              {selectedExam.note && (
                <Card.Text>
                  <strong>Note:</strong> {selectedExam.note}
                </Card.Text>
              )}
              {selectedExam.statino_presente && (
                <Button 
                  variant="outline-primary" 
                  onClick={() => handleDownloadStatino(selectedExam.id_calendario_esame_utente, selectedExam?.nome_file_statino)}
                  className="mt-2"
                >
                  <i className="bi bi-download me-2"></i> Scarica Statino
                </Button>
              )}
            </Card.Body>
          </Card>
        );
      }
      return null;
    };

    const handleDownloadStatino = async (idCalendarioEsameUtente, filename) => {
      try {
        await EsamiUtentiService.downloadStatino({ id_calendario_esame_utente: idCalendarioEsameUtente, filename: filename });
      } catch (error) {
        alertService.error('Errore nel download dello statino');
      }
    };

    switch (bookingStep) {
      case 'initial':
        return (
          <div className="exam-details">
            <h4 className="mb-4">Dettagli Esame</h4>
            <p><strong>Data:</strong> {moment(selectedExam.start).format('dddd D MMMM YYYY')}</p>
            <p><strong>Orario:</strong> {moment(selectedExam.start).format('HH:mm')} - {moment(selectedExam.end).format('HH:mm')}</p>
            {isUserExamBooked(selectedExam) ? (
              <>
                <p><strong>Data Prenotazione:</strong> {moment(selectedExam.data_prenotazione).format('DD/MM/YYYY HH:mm')}</p>
                {moment(selectedExam.start).diff(moment(), 'hours') >= 24 ? (
                  <>
                    <p className="text-muted" style={{ fontSize: '0.9rem' }}>
                      Puoi annullare la tua prenotazione fino a 24 ore prima dell'inizio dell'esame.
                    </p>
                    <Button 
                      variant="danger" 
                      className="mt-3"
                      onClick={openCancelModal}
                    >
                      Annulla Prenotazione
                    </Button>
                  </>
                ) : (
                  <p className="text-danger" style={{ fontSize: '0.9rem' }}>
                    Non è possibile annullare la tua prenotazione all'esame.
                  </p>
                )}
                {renderExamResult()}
              </>
            ) : (
              <>
                <AlertEmailConfermaPrenotazioneEsame />
                <Button 
                  variant="primary" 
                  onClick={handleBookExam} 
                  className="mt-3"
                  disabled={loadingStateEsamiUtenti.isLoadingDeviceTestPrenotazione || loadingStateEsamiUtenti.isLoadingEsameUtenteCreating}
                >
                  {loadingStateEsamiUtenti.isLoadingDeviceTestPrenotazione ? 'Avvio test...' : 'Prenota Esame'}
                </Button>
              </>
            )}
          </div>
        );
      case 'confirm':
        return (
          <div className="exam-confirmation">
            <h4 className="mb-4">Conferma Prenotazione</h4>
            <Alert variant="success" className="mb-3 py-2">
              <small><i className="bi bi-check-circle-fill me-1"></i> Test dispositivo (audio, video e browser) superato con successo.</small>
            </Alert>
            <AlertEmailConfermaPrenotazioneEsame />
            <p>Sei sicuro di voler prenotare l'esame per il giorno {moment(selectedExam.start).format('DD/MM/YYYY')} dalle {moment(selectedExam.start).format('HH:mm')} alle {moment(selectedExam.end).format('HH:mm')}?</p>
            <div className="d-flex justify-content-between mt-4">
              <Button variant="secondary" onClick={() => { stopDeviceTestPolling(); setBookingStep('initial'); }}>
                Indietro
              </Button>
              <CustomButton 
                variant="success"
                label={'Conferma Prenotazione'}
                isLoading={loadingStateEsamiUtenti.isLoadingEsameUtentePrenotazione}
                onClick={handleConfirmBooking}
                disabled={loadingStateEsamiUtenti.isLoadingEsameUtenteCreating}
              />
            </div>
          </div>
        );
      case 'success':
        return (
          <div className="exam-success">
            <Alert variant="success" className="mb-4">
              <h4 className="alert-heading">Prenotazione Confermata!</h4>
              <p className="mb-2">
                Hai prenotato con successo l&apos;esame per il giorno{' '}
                {moment(selectedExam.start).format('DD/MM/YYYY')} dalle{' '}
                {moment(selectedExam.start).format('HH:mm')} alle {moment(selectedExam.end).format('HH:mm')}.
              </p>
              <p className="mb-0 small">
                Controlla la tua casella di posta: ti abbiamo inviato l&apos;email di conferma che fa fede
                per la prenotazione e che include tutte le spiegazioni alle quali dovrai attenerti.
              </p>
            </Alert>
            <Button variant="primary" onClick={handleCloseModal}>
              Chiudi
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  const getBackPath = () => {
    if (userLoggedIn?.role === ROLES.TUTOR) {
      return "/area-personale/classi"
    } else {
      if (classe?.corso?.modalita_erogazione === TIPO_CORSO.TELEMATICA_ASINCRONA) {
        return "/area-personale/corso"
      } else {
        return "/area-personale/classe"
      }
    }
  }

  /*  
  const examsPrenotati = exams.filter(exam => exam.prenotato === true);
  const examsPrenotatiFromDB = exams.filter(exam => exam.myPrenotation === 1);
  const userHasPrenotato = examsPrenotati.length > 0;
  const userHasPrenotatoFromDB = examsPrenotatiFromDB.length > 0;
  */

  const hasBlockingReservation = exams.some(exam => {
        const isMyExam = isUserExamBooked(exam);
        if (!isMyExam) return false;

        // Se è stato PROMOSSO -> Blocca sempre (ha finito)
        if (exam.esito_esame === 'promosso') return true;

        // Se è in ATTESA (nessun esito) -> Blocca (deve aspettare il risultato)
        if (!exam.esito_esame) return true;

        // Se è BOCCIATO/ASSENTE -> Blocca SOLO SE l'admin NON ha abilitato la ripetizione
        if ((exam.esito_esame === 'bocciato' || exam.esito_esame === 'non_svolto') && exam.abilitato_ripetizione != 1) {
            return true;
        }

        // Se arriviamo qui (es. Bocciato MA Abilitato=1), NON blocca
        return false;
    });

  return (
    <div className={`card custom-card w-100 shadow ${isSmallScreen ? 'py-3 px-0' : 'p-5'}`} style={{ minHeight: "700px" }}>
      <div className="container">
        <ComebackArrow path={getBackPath()} state={{ classe }} />
        <div className="d-flex justify-content-between custom-card-header mb-3">
          <div className="d-flex gap-3 custom-card-title my-2">
            <div className="custom-icon-container">
              {classe?.corso?.immagine_corso ? 
                <img src={classe?.corso?.immagine_corso} className='custom-icon' alt='immagine corso'/> : 
                <i className="bi custom-icon bi-calendar-event fs-2"></i>}
            </div>
            <div className="align-self-center">
              <span className="fs-4 d-block">
                <i><b><h2>{classe?.corso?.denominazione} - Esami</h2></b></i>
              </span>
            </div>
          </div>
        </div>
        <div style={{ height: '100vh' }}>
          <div className="mt-4 mb-4">
            <div className="card border-info bg-light shadow-sm">
                <div className="card-body d-flex align-items-center p-4">
                    {/* Icona laterale */}
                    <div className="me-4 text-info d-none d-sm-block">
                        <i className="bi bi-calendar-check" style={{ fontSize: '2.5rem' }}></i>
                    </div>
                    
                    {/* Testo */}
                    <div>
                        <h5 className="fw-bold text-info mb-2">
                            <i className="bi bi-info-circle-fill me-2 d-sm-none"></i>
                            Se la data di esame non è adatta alle tue esigenze
                        </h5>
                        <p className="mb-0 text-muted">
                            puoi acquistare al costo di <strong>30,00€</strong> un 
                            <span className="text-dark fw-bold"> Esame Straordinario</span> e concordare direttamente con il tuo tutor data e ora compatibili con le tue disponibilità.
                        </p>
                    </div>
                    
                    {/* (Opzionale) Bottone azione se hai una pagina di acquisto */}
                    {/* <div className="ms-auto ps-3">
                        <Button variant="info" className="text-white fw-bold text-nowrap">
                            Richiedi Ora
                        </Button>
                    </div>
                    */}
                </div>
            </div>
            {userLoggedIn?.role !== ROLES.TUTOR && (
              <div className="card border-primary bg-white shadow-sm mb-4">
                <div className="card-body d-flex align-items-start p-4">
                  <div className="me-3 text-primary d-none d-sm-block">
                    <i className="bi bi-envelope-check" style={{ fontSize: '2rem' }} />
                  </div>
                  <div>
                    <h5 className="fw-bold text-primary mb-2">
                      <i className="bi bi-envelope-check me-2 d-sm-none" />
                      Conferma via email
                    </h5>
                    <p className="mb-0 text-muted small">
                      Dopo la prenotazione riceverai un&apos;email di conferma: è quella che{' '}
                      <strong className="text-dark">fa fede per la prenotazione</strong> e contiene
                      tutte le istruzioni e le spiegazioni alle quali dovrai attenerti.
                    </p>
                  </div>
                </div>
              </div>
            )}
        </div>
        <CustomCalendar
          events={
            exams.length === 0 
              ? []
              : exams
                  .filter(exam => {
                    const hoursToExam = moment(exam.start).diff(moment(), 'hours');
                    const isMyExam = isUserExamBooked(exam);
                    const isGruppo = exam.is_multiplo == 1 || (exam.max_parts && exam.max_parts > 1);

                    if (userLoggedIn?.role !== ROLES.TUTOR) {
                      // 1. Se è un mio esame (passato o futuro), mostramelo SEMPRE (per vedere lo storico)
                      if (isMyExam) return true;

                      // 2. Se ho una prenotazione "bloccante" attiva, NASCONDI tutti gli altri esami futuri
                      if (hasBlockingReservation) return false;

                      // 3. Altrimenti (sono libero), mostra gli esami futuri prenotabili (regola 24h)
                      if (isGruppo) {
                          // SE È UN GRUPPO: Nessun blocco 24h. 
                          // Basta che l'esame non sia ancora iniziato (hoursToExam >= 0 o logica più fine)
                          return hoursToExam >= 1; 
                      } else {
                          // SE È SINGOLO: Mantieni il blocco delle 24 ore
                          return hoursToExam >= 24;
                      }
                    }

                    return true;
                  })
                  .map(exam => {
                    const isGruppo = exam.is_multiplo == 1 || (exam.max_parts && exam.max_parts > 1);
                    let baseTitle = "Esame Disponibile";
                    if (userLoggedIn?.role !== ROLES.TUTOR && isUserExamBooked(exam)) {
                      if (exam.presenza === 'assente') {
                        baseTitle = "Assente";
                      } else if (exam.esito_esame && exam.esito_esame !== 'non_svolto') {
                        baseTitle = exam.esito_esame === 'promosso' ? "Promosso" : "Bocciato";
                      } else {
                        baseTitle = "Esame Prenotato";
                      }
                    } else if (userLoggedIn?.role === ROLES.TUTOR) {
                      baseTitle = `Esame (${exam.utenti?.filter(u => u.prenotato)?.length || 0}/${exam.utenti?.length || 0} prenotati)`;
                    }

                    if (isGruppo && userLoggedIn?.role !== ROLES.TUTOR && !isUserExamBooked(exam)) {
                        const iscritti = exam.utenti?.filter(u => u.prenotato).length || 0;
                        baseTitle = `Esame Gruppo (${iscritti}/${exam.max_parts || '?'})`;
                    }

                    const countdownText = examCountdowns[exam.id_calendario_esame];
                    let subtitle = null;
                    let agendaAccessFooter = null;

                    const isExamBooked = isUserExamBooked(exam);
                    const hasNotStarted = countdownText && countdownText !== 'INIZIATO';
                    const isAlreadyTaken = exam.esito_esame && exam.esito_esame !== 'non_svolto';
                    const sessioneChiusa = isSessioneEsameStorica(exam);
                    const canCancelBooking = moment(exam.start).diff(moment(), 'hours') >= 24;

                    if (userLoggedIn?.role !== ROLES.TUTOR && isExamBooked) {
                      const bookedStatusLabel = (() => {
                        if (exam.presenza === 'assente') return 'Assente';
                        if (exam.esito_esame && exam.esito_esame !== 'non_svolto') {
                          return exam.esito_esame === 'promosso' ? 'Promosso' : 'Bocciato';
                        }
                        return 'Prenotazione confermata';
                      })();

                      agendaAccessFooter = (
                        <div className="text-start w-100">
                          <div className={`small mb-2 ${bookedStatusLabel === 'Prenotazione confermata' ? 'text-success' : 'text-dark'}`}>
                            <i className={`bi ${bookedStatusLabel === 'Prenotazione confermata' ? 'bi-check-circle-fill' : 'bi-info-circle-fill'} me-1`}></i>
                            <strong>{bookedStatusLabel}</strong>
                            {exam.data_prenotazione && bookedStatusLabel === 'Prenotazione confermata' && (
                              <span className="text-muted">
                                {' '}— prenotato il {moment(exam.data_prenotazione).format('DD/MM/YYYY HH:mm')}
                              </span>
                            )}
                          </div>
                          {hasNotStarted && (
                            <div className="text-muted small mb-2">{countdownText}</div>
                          )}
                          {countdownText === 'INIZIATO' && sessioneChiusa && !isAlreadyTaken && (
                            <div className="text-muted small mb-2">
                              <i className="bi bi-check-all me-1"></i>
                              Sessione terminata - in attesa di esito
                            </div>
                          )}
                          {countdownText === 'INIZIATO' && !isAlreadyTaken && !sessioneChiusa && (
                            <Button
                              variant="success"
                              size="sm"
                              className="fw-bold d-inline-flex align-items-center justify-content-center text-wrap text-start w-100"
                              onClick={(e) => {
                                e.stopPropagation();
                                enterJoinRoom(exam.id_calendario_esame);
                              }}
                            >
                              <i className="bi bi-camera-video-fill me-2 fs-5 flex-shrink-0 ignore-calendar-click"></i>
                              Accedi
                            </Button>
                          )}
                          {canCancelBooking && !isAlreadyTaken && countdownText !== 'INIZIATO' && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="fw-bold d-inline-flex align-items-center justify-content-center text-wrap text-start w-100"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedExam(exam);
                                setShowCancelModal(true);
                              }}
                            >
                              <i className="bi bi-x-circle me-2 flex-shrink-0"></i>
                              Annulla prenotazione
                            </Button>
                          )}
                          {!canCancelBooking && !isAlreadyTaken && countdownText !== 'INIZIATO' && (
                            <div className="text-muted small">
                              L&apos;annullamento non è più consentito entro 24 ore dall&apos;inizio dell&apos;esame.
                            </div>
                          )}
                        </div>
                      );
                    } else if (userLoggedIn?.role !== ROLES.TUTOR && !isExamBooked) {
                      agendaAccessFooter = (
                        <Button
                          variant="primary"
                          size="sm"
                          className="fw-bold d-inline-flex align-items-center justify-content-center text-wrap text-start w-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            startDeviceTestForBooking(exam);
                          }}
                        >
                          <i className="bi bi-calendar-plus me-2 fs-5 flex-shrink-0"></i>
                          Prenota esame
                        </Button>
                      );
                    }

                    return {
                      ...exam,
                      title: (
                        <div>
                          <strong>{baseTitle}</strong>
                          {subtitle && <div>{subtitle}</div>}
                        </div>
                      ),
                      agendaAccessFooter
                    };
                  })
          }
          availableViews={["agenda"]}
          customToolbar={() => (
            <div className="rbc-toolbar d-flex justify-content-center">
              <h4 className="text-primary">Esami disponibili</h4>
            </div>
          )}
          onSelectEvent={handleSelectEvent}
          eventCursor="pointer"
          slotCursor="default"
          minDate={minDate}
          maxDate={null}
          selectable={false}
          customEventPropGetter={customEventPropGetter}
          agendaHideEventColumn
          agendaTimeStartOnly
        />

          {/* <CustomCalendar
            events={exams.map(exam => {
              let title = userLoggedIn?.role === ROLES.TUTOR
                ? `Esame (${exam.utenti.filter(u => u.prenotato).length}/${exam.utenti.length} prenotati)`
                : "Esame Disponibile";

              if (userLoggedIn?.role !== ROLES.TUTOR && exam.prenotato) {
                if (exam.presenza === 'assente') {
                  title = "Assente";
                } else if (exam.esito_esame && exam.esito_esame !== 'non_svolto') {
                  title = exam.esito_esame === 'promosso' ? "Promosso" : "Bocciato";
                } else {
                  title = "Esame Prenotato";
                }
              }

              return {
                ...exam,
                title,
              };
            })}
            onSelectEvent={handleSelectEvent}
            eventCursor="pointer"
            slotCursor="default"
            minDate={minDate}
            maxDate={null}
            selectable={false}
            customEventPropGetter={customEventPropGetter}
          /> */}
        {/* AVVISO ESAME STRAORDINARIO */}

          
        </div>
        <CustomModal
          show={showModal}
          onHide={handleCloseModal}
          headerText={
            <h5 className="form-control-static">
              {selectedExam && moment(selectedExam.start).format('dddd D MMMM YYYY')}
            </h5>
          }
          size={userLoggedIn?.role === ROLES.TUTOR ? "xl" : "md"}
          body={renderModalContent()}
          btnCancelText="Chiudi"
          closeButton={true}
          hideButtons={true}
        />
        <CustomModal
          show={showCancelModal}
          onHide={() => setShowCancelModal(false)}
          headerText="Conferma Annullamento Prenotazione"
          body={
            <p className="mb-0">Sei sicuro di voler annullare la tua prenotazione?</p>
          }
          onSave={confirmCancelBooking}
          onUndoChanges={() => setShowCancelModal(false)}
          btnText="Conferma Annullamento"
          btnCancelText="Annulla"
          variant="danger"
          size="md"
          hideSaveBtn={false}
          closeButton={true}
        />
        <CustomModal
          show={showErrorModal}
          onHide={() => setShowErrorModal(false)}
          headerText={<h5 className="text-warning">Attenzione</h5>}
          size="md"
          body={
            <p>{errorMessage}</p>
          }
          btnCancelText={"Chiudi"}
          onUndoChanges={() => setShowErrorModal(false)}
          hideSaveBtn={true} // Nascondiamo il pulsante di salvataggio
        />
        {/* MODALE ACCESSO NEGATO */}
        <CustomModal
          show={showAccessDeniedModal}
          onHide={() => setShowAccessDeniedModal(false)}
          headerText={
            <h5 className="text-danger mb-0">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                Accesso Negato
            </h5>
          }
          size="md"
          hideSaveBtn={true} 
          btnCancelText="Ho capito"
          body={
            <div className="text-center py-3">
                <div className="mb-3">
                    <i className="bi bi-door-closed-fill text-danger" style={{ fontSize: '3rem' }}></i>
                </div>
                <h5 className="fw-bold text-danger">Impossibile accedere all'esame</h5>
                <p className="fs-5 mt-3">{accessDeniedMessage}</p>
                <div className="alert alert-secondary mt-3 mb-0 text-start small">
                    <i className="bi bi-info-circle me-1"></i>
                    Nota: ti è stata inviata un'email di notifica all'indirizzo associato al tuo account.
                </div>
            </div>
          }
          closeButton={true}
          onUndoChanges={() => setShowAccessDeniedModal(false)}
        />
        {/* MODALE TEST DISPOSITIVO JAMPINK (prenotazione esame) */}
        <CustomModal
          show={showDeviceTestModal}
          onHide={() => {
            stopDeviceTestPolling();
            setShowDeviceTestModal(false);
            setDeviceTestUrl("");
          }}
          headerText={
            <h5 className="text-primary mb-0">
              <i className="bi bi-camera-video me-2"></i>
              Verifica dispositivo
            </h5>
          }
          size="md"
          hideSaveBtn={true}
          btnCancelText="Annulla test"
          body={
            <div className="text-center py-3">
              <div className="mb-3">
                <i className="bi bi-headset text-primary" style={{ fontSize: '3rem' }}></i>
              </div>
              <p className="fs-6 mb-4">
                Prima di prenotare l&apos;esame devi verificare microfono, webcam e browser su Jampink.
                Clicca il pulsante qui sotto per aprire la procedura di test in una nuova scheda.
              </p>
              <div className="alert alert-warning text-start small mb-4">
                <i className="bi bi-exclamation-triangle me-1"></i>
                Se Jampink segnala <strong>criticità</strong> (audio, video o browser non idonei), correggile
                oppure ripeti il test con <strong>un altro dispositivo</strong>. La prenotazione resta bloccata
                finché il test non risulta superato.
              </div>
              <a
                href={deviceTestUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary btn-lg fw-bold"
                onClick={markDeviceTestJampinkOpened}
              >
                <i className="bi bi-box-arrow-up-right me-2"></i>
                Avvia test su Jampink
              </a>
              {deviceTestPolling && (
                <div className="mt-4 text-muted small">
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  In attesa del completamento del test… Puoi chiudere la scheda Jampink quando hai finito: rileviamo automaticamente l&apos;esito.
                </div>
              )}
              <div className="alert alert-info mt-4 mb-0 text-start small">
                <i className="bi bi-info-circle me-1"></i>
                Lascia aperta questa pagina. Al termine del test potrai confermare la prenotazione.
              </div>
            </div>
          }
          closeButton={true}
          onUndoChanges={() => {
            stopDeviceTestPolling();
            setShowDeviceTestModal(false);
            setDeviceTestUrl("");
          }}
        />
        {/* MODALE TEST DISPOSITIVO NON SUPERATO (solo al ritorno da Jampink) */}
        <CustomModal
          show={showDeviceTestFailedModal}
          onHide={() => setShowDeviceTestFailedModal(false)}
          headerText={
            <h5 className="text-danger mb-0">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              Test dispositivo non superato
            </h5>
          }
          size="md"
          hideSaveBtn={true}
          btnCancelText="Ho capito"
          body={
            <div className="text-center py-3">
              <div className="mb-3">
                <i className="bi bi-camera-video-off text-danger" style={{ fontSize: '3rem' }}></i>
              </div>
              <p className="fs-6 mb-3">
                La verifica su Jampink <strong>non risulta completata con successo</strong>.
              </p>
              <div className="alert alert-warning text-start small mb-0">
                <i className="bi bi-info-circle me-1"></i>
                Risolvi le criticità segnalate (microfono, webcam o browser) oppure ripeti il test con{' '}
                <strong>un altro dispositivo</strong>, poi clicca di nuovo <strong>«Prenota esame»</strong>.
              </div>
            </div>
          }
          closeButton={true}
          onUndoChanges={() => setShowDeviceTestFailedModal(false)}
        />
        {/* MODALE APRI STANZA VIRTUALE - usa anchor target=_blank per evitare
            sia il popup blocker (è un click utente diretto su <a>) sia
            problemi di reverse proxy (HighEdu resta nella scheda originale). */}
        <CustomModal
          show={showOpenRoomModal}
          onHide={() => setShowOpenRoomModal(false)}
          headerText={
            <h5 className="text-success mb-0">
              <i className="bi bi-camera-video-fill me-2"></i>
              Stanza virtuale pronta
            </h5>
          }
          size="md"
          hideSaveBtn={true}
          btnCancelText="Annulla"
          body={
            <div className="text-center py-3">
              <div className="mb-3">
                <i className="bi bi-door-open-fill text-success" style={{ fontSize: '3rem' }}></i>
              </div>
              <p className="fs-5 mb-4">
                La stanza virtuale per il tuo esame è pronta.<br />
                Clicca il pulsante qui sotto per accedere.
              </p>
              <a
                href={meetingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-success btn-lg fw-bold"
                onClick={() => setShowOpenRoomModal(false)}
              >
                <i className="bi bi-box-arrow-up-right me-2"></i>
                Apri stanza virtuale
              </a>
              <div className="alert alert-info mt-4 mb-0 text-start small">
                <i className="bi bi-info-circle me-1"></i>
                La stanza virtuale si aprirà in una nuova scheda del browser.
                Lascia aperta questa pagina: potrai tornare qui al termine dell'esame.
              </div>
            </div>
          }
          closeButton={true}
          onUndoChanges={() => setShowOpenRoomModal(false)}
        />
      </div>
    </div>
  );
}
